import { debounce } from 'lodash';
import * as OverlayScrollbars from 'overlayscrollbars';

import Splide, { ComponentConstructor, Components } from '@splidejs/splide';

import { LARGE_MAX, MEDIUM_MAX, SMALL_MAX } from '../constants/breakpoints';
import { Tabs } from './tabs';

const SHOW_SLIDES_CLASS = 'show';
const SHOW_NAV_BUTTONS_CLASS = 'show';
const SLIDER_ATTRIBUTE = 'data-recommendation-slider';

const SPLIDE_INTERACTIVITY = {
    INTERACTIVE: {
        keyboard: true,
        drag: true,
        pagination: true,
        arrows: true,
        slideFocus: true,
    },
    NOT_INTERACTIVE: {
        keyboard: false,
        drag: false,
        pagination: false,
        arrows: false,
        slideFocus: false,
    },
};

const convertCSSClassStringToClassSelector = (classString: string): string => {
    const reducer = (classes: Array<string>, classFragment: string) => {
        const fragment = classFragment.trim();
        if (fragment) {
            classes.push(`.${fragment}`);
        }
        return classes;
    };
    const selector = classString.split(' ').reduce(reducer, []).join('');
    return selector;
};
const getClassSelectorsFromStrings = (...args) => {
    return (args as Array<string>).map((argument) =>
        convertCSSClassStringToClassSelector(argument)
    );
};
const matchParentHeightWithContentFactory = (contentElement: HTMLElement) => {
    let start: DOMHighResTimeStamp = null;
    const TIMEOUT = 2000;
    function updateHeight(timeStamp: DOMHighResTimeStamp) {
        if (!start) {
            start = timeStamp;
        }
        const elapsedTime = timeStamp - start;
        // Stop the update to prevent infinite loops
        if (elapsedTime > TIMEOUT) {
            return;
        }
        if (!contentElement) {
            return;
        }
        contentElement.parentElement.style.height = `${contentElement.offsetHeight}px`;
        setTimeout(() => {
            window.requestAnimationFrame(updateHeight);
        }, 200);
    }
    updateHeight.reset = () => {
        // Reset the start timestamp to allow new update
        start = null;
    };
    updateHeight.cancel = () => {
        //Set the start timestamp to negative TIMEOUT to prevent any new update
        start = -TIMEOUT;
    };
    return updateHeight;
};

type RecommendationBlockSplideExtensionFactory = (
    tabPanel: HTMLElement
) => ComponentConstructor;
const RecommendationBlockSplideExtensionFactory: RecommendationBlockSplideExtensionFactory =

        (tabPanel: HTMLElement): ComponentConstructor =>
        (Splide: Splide, Components: Components) => {
            const { slides, root } = Components.Elements;
            let arrowContainerMoved = false;

            Splide.on('mounted arrows:mounted pagination:mounted', () => {
                updateLayout();
            });
            Splide.on('destroy', () => {
                window.removeEventListener('resize', updateOnResize);
                updateTabPanelHeight.cancel();
            });

            function mount() {
                updateOptions();
                window.addEventListener('resize', updateOnResize);
            }

            const updateOnResize = debounce(() => {
                updateLayout();
                updateOptions();
            }, 100);

            const updateTabPanelHeight =
                matchParentHeightWithContentFactory(tabPanel);

            function updateLayout() {
                if (!slides || !slides.length) {
                    return;
                }
                updateTabPanelHeight.reset();
                requestAnimationFrame(updateTabPanelHeight);
                const { pagination, prev, next } =
                    Splide.options?.classes || {};
                if (!pagination || !prev || !next) {
                    return;
                }
                const [paginationSelector, prevSelector, nextSelector] =
                    getClassSelectorsFromStrings(pagination, prev, next);
                const paginationContainer = root.querySelector(
                    paginationSelector
                ) as HTMLElement;
                const prevButton = root.querySelector(
                    prevSelector
                ) as HTMLElement;
                const nextButton = root.querySelector(
                    nextSelector
                ) as HTMLElement;
                if (!prevButton || !nextButton) {
                    return;
                }
                const arrowContainer = nextButton.parentElement;
                if (!arrowContainer) {
                    return;
                }
                arrowContainer.classList.remove(SHOW_NAV_BUTTONS_CLASS);
                if (!paginationContainer) {
                    return;
                }
                reorderNavigationElements(
                    paginationContainer,
                    arrowContainer,
                    nextButton
                );
                arrowContainer.classList.add(SHOW_NAV_BUTTONS_CLASS);
            }

            /**
             * move prev, next button and pagination elements to make UI and accessibility order consistent
             */
            function reorderNavigationElements(
                paginationContainer: HTMLElement,
                arrowContainer: HTMLElement,
                nextButton: HTMLElement
            ) {
                if (!arrowContainerMoved) {
                    paginationContainer.parentNode.insertBefore(
                        arrowContainer,
                        paginationContainer
                    );
                    arrowContainerMoved = true;
                }
                nextButton.parentNode.insertBefore(
                    paginationContainer,
                    nextButton
                );
            }

            function updateOptions() {
                if (!slides || !slides.length) {
                    return;
                }
                if (!isInteractableCarousel()) {
                    Splide.options = {
                        ...Splide.options,
                        ...SPLIDE_INTERACTIVITY.NOT_INTERACTIVE,
                    };
                } else {
                    Splide.options = {
                        ...Splide.options,
                        ...SPLIDE_INTERACTIVITY.INTERACTIVE,
                    };
                }
            }

            function isInteractableCarousel() {
                if (!slides || !slides.length) {
                    return false;
                }
                const slideNumberPerPage: number = Splide.options?.perPage || 0;
                return slides.length > slideNumberPerPage;
            }

            return {
                mount,
            };
        };

export class Recommendation {
    private recommendationBlock: HTMLElement;
    private activeSplide: Splide;
    private activePanel: HTMLElement;
    private splideMap: { [key: string]: Splide } = {};
    private tabListScrollbar: OverlayScrollbars;
    private tabs: Tabs;

    constructor(recommendationBlock: HTMLElement) {
        this.recommendationBlock = recommendationBlock;
        this.initialize();
    }

    initialize() {
        this.tabs = new Tabs({ container: this.recommendationBlock });
        this.tabListScrollbar = OverlayScrollbars(
            this.recommendationBlock.querySelector('[role="tablist"]'),
            { overflowBehavior: { x: 'scroll', y: 'hidden' } }
        ) as OverlayScrollbars;
        this.initializeActiveTab();
        this.tabs.on('tabSwitch', () => {
            this.initializeActiveTab();
        });
    }

    private initializeActiveTab() {
        if (this.activeSplide) {
            this.activeSplide.destroy();
        }
        if (this.activePanel) {
            this.activePanel.classList.remove(SHOW_SLIDES_CLASS);
        }
        const {
            panelId: activePanelId,
            panel: activePanel,
            tab: activeTab,
        } = this.tabs.getActiveTabInfo();
        if (!activeTab) {
            return;
        }
        if (this.tabListScrollbar) {
            this.tabListScrollbar.scroll({
                el: activeTab,
                scroll: {
                    x: 'ifneeded',
                    y: 'never',
                },
            });
        }
        if (!activePanel) {
            return;
        }
        this.activateTheme(activePanel);
        const slider = activePanel.querySelector(
            `[${SLIDER_ATTRIBUTE}]`
        ) as HTMLElement;
        if (!slider) {
            return;
        }
        this.initializeSlider(slider, activePanelId, activePanel);
        this.activePanel = activePanel;
        activePanel.classList.add(SHOW_SLIDES_CLASS);
    }

    private initializeSlider(
        slider: HTMLElement,
        activePanelId: string,
        activePanel: HTMLElement
    ) {
        let splide = this.splideMap[activePanelId];
        if (!splide) {
            const padding = 'var(--standard-padding-h)';
            splide = new Splide(slider, {
                autoWidth: true,
                padding,
                lazyLoad: 'nearby',
                preloadPages: 1,
                classes: {
                    arrow: 'splide__arrow btn',
                    page: 'splide__pagination__page btn',
                },
                gap: 'calc(var(--grid-half-gutter-x) * 2)',
                wheel: false,
                perPage: 4,
                perMove: 1,
                breakpoints: {
                    [LARGE_MAX]: {
                        perPage: 3,
                        padding,
                    },
                    [MEDIUM_MAX]: {
                        perPage: 2,
                        padding,
                    },
                    [SMALL_MAX]: {
                        perPage: 1,
                        padding: 'var(--recommendation-block-track-padding-sm)',
                    },
                },
            });
        }
        const RecommendationBlockSplideExtension: ComponentConstructor =
            RecommendationBlockSplideExtensionFactory(activePanel);
        splide.mount({
            RecommendationBlockSplideExtension,
        });
        this.activeSplide = splide;
        this.splideMap[activePanelId] = splide;
    }

    private activateTheme(activePanel: HTMLElement) {
        const blockClassList = this.recommendationBlock.classList;
        const activeTheme = activePanel.getAttribute(
            'data-recommendation-tab-theme' || ''
        );
        if (activeTheme) {
            let activeThemeActivated = false;
            blockClassList.forEach((className) => {
                if (className.indexOf('theme-') !== -1) {
                    blockClassList.remove(className);
                    blockClassList.add(activeTheme);
                    activeThemeActivated = true;
                }
            });
            if (!activeThemeActivated) {
                blockClassList.add(activeTheme);
            }
        }
        if (!this.tabListScrollbar) {
            return;
        }
        if (!activeTheme || activeTheme === 'theme-white') {
            this.tabListScrollbar.options({ className: 'os-theme-dark' });
        } else {
            this.tabListScrollbar.options({ className: 'os-theme-light' });
        }
    }
}
